import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    useSelector } from "react-redux";
import {
  CART_INITIAL_STATE,
  GETACCEMPDETAILS,
  GET_CONVERSIONTYPE_DATA,
  isRoleAllowed,
  SHOWFOODITEMSFORSTOCK,
  SHOWRESTOFEWDATA,
  SUCCESS,
  ROLE_CREATE,
  INVSTOCKMGMT,
  FOODITEMPRODUCTMGMT
} from "../../constants/constant";
import { snpAxios } from "../global/api";
import Loader from "../global/loader/Loader";
import "./ReceipeV2.css";

const emptyIngredient = () => ({ stockId: "", conversionId: "", quantity: "", wastagePercent: "0", optional: false, notes: "" });
const emptyStep = (stepNo = 1) => ({ stepNo, title: "", instruction: "", durationMinutes: "0" });
const emptyForm = () => ({
  id: "", stockId: "", receipyById: "", quantityType: "Full", quantity: 1,
  preparationTimeMinutes: "0", preparationNotes: "", ingredients: [emptyIngredient()],
  preparationSteps: [emptyStep()], originalIngredientIds: [],
});
const values = (response, key = "value_2") => response?.data?.jcJson?.[key] || [];
const messageOf = (error, fallback) => error?.response?.data?.errorMessage || error?.message || fallback;

export default function ReceipeV2() {
  const api = useSelector((state) => state.setApiUrlReducer.MAINURL.INITIAL_BASE_URL)?.BACKEND_BASE_URL;
  const shop = useSelector((state) => state.setActiveShopHashReducer.activeShopHash);
  const [receipes, setReceipes] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [units, setUnits] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [foodQuantityTypes, setFoodQuantityTypes] = useState({ Full: 1, Half: 0.5, Quarter: 0.25, Other: 0 });
  const [form, setForm] = useState(emptyForm());
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [notice, setNotice] = useState({ text: "", error: false });

  const params = useMemo(() => ({ apiToken: CART_INITIAL_STATE.apiToken, shopHash: shop?.id }), [shop?.id]);
  const postLookup = useCallback((path, extra = {}) => snpAxios.post(`${api}${path}`, {
    jcJson: { ...params, ...extra },
  }), [api, params]);

  const loadReceipes = useCallback(async () => {
    const response = await snpAxios.get(`${api}receipe`, { params });
    if (response.data.status === SUCCESS) {
      setReceipes(values(response, "data"));
      setFoodQuantityTypes(response.data.jcJson.foodQuantityType || {});
    } else setReceipes([]);
  }, [api, params]);

  const loadPage = useCallback(async () => {
    if (!api || !shop?.id) return;
    setLoading(true);
    setNotice({ text: "", error: false });
    try {
      const [dishResponse, stockResponse, unitResponse, employeeResponse] = await Promise.all([
        postLookup("resto/" + SHOWRESTOFEWDATA),
        postLookup("resto/" + SHOWFOODITEMSFORSTOCK, { checkForStock: true }),
        postLookup("resto/" + GET_CONVERSIONTYPE_DATA, { createNew: false }),
        postLookup(GETACCEMPDETAILS, { roles: "" }),
      ]);
      setDishes(values(dishResponse));
      setStocks(values(stockResponse));
      setUnits(values(unitResponse));
      setEmployees(values(employeeResponse));
      await loadReceipes();
    } catch (error) {
      setNotice({ text: messageOf(error, "Receipes could not be loaded."), error: true });
    } finally {
      setLoading(false);
    }
  }, [api, loadReceipes, postLookup, shop?.id]);

  useEffect(() => { loadPage(); }, [loadPage]);

  const unitName = useCallback((id) => {
    const unit = units.find((item) => item.id === id);
    if (!unit) return "unit";
    return unit.unitRestrictions ? unit.unitName : (unit.smallUnitName || unit.unitName);
  }, [units]);

  const openNew = () => {
    if (!isRoleAllowed([INVSTOCKMGMT, FOODITEMPRODUCTMGMT], ROLE_CREATE)) {
      setNotice({ text: "Ask the owner for permission to add a receipe.", error: true });
      return;
    }
    setForm(emptyForm());
    setOpen(true);
  };

  const openEdit = async (id) => {
    setSaving(true);
    try {
      const response = await snpAxios.get(`${api}receipe/${id}`, { params });
      if (response.data.status !== SUCCESS) throw new Error("Receipe could not be opened.");
      const item = response.data.jcJson.data;
      const ingredients = (item.ingredients?.length ? item.ingredients : item.listItems || []).map((ingredient) => ({
        ...emptyIngredient(), ...ingredient, quantity: String(ingredient.quantity ?? ""),
        wastagePercent: String(ingredient.wastagePercent ?? 0),
      }));
      const steps = (item.preparationSteps || []).map((step, index) => ({
        ...emptyStep(index + 1), ...step, durationMinutes: String(step.durationMinutes ?? 0),
      }));
      setForm({
        ...emptyForm(), ...item, receipyById: item.receipeById,
        ingredients: ingredients.length ? ingredients : [emptyIngredient()],
        preparationSteps: steps.length ? steps : [emptyStep()],
        originalIngredientIds: ingredients.map((ingredient) => ingredient.stockId),
      });
      setOpen(true);
    } catch (error) {
      setNotice({ text: messageOf(error, "Receipe could not be opened."), error: true });
    } finally { setSaving(false); }
  };

  const setIngredient = (index, key, value) => setForm((current) => ({
    ...current, ingredients: current.ingredients.map((ingredient, row) => row === index ? { ...ingredient, [key]: value } : ingredient),
  }));
  const removeIngredient = (index) => setForm((current) => ({
    ...current, ingredients: current.ingredients.filter((_, row) => row !== index),
  }));
  const setStep = (index, key, value) => setForm((current) => ({
    ...current, preparationSteps: current.preparationSteps.map((step, row) => row === index ? { ...step, [key]: value } : step),
  }));
  const removeStep = (index) => setForm((current) => ({
    ...current, preparationSteps: current.preparationSteps.filter((_, row) => row !== index)
      .map((step, row) => ({ ...step, stepNo: row + 1 })),
  }));

  const validate = () => {
    if (!form.stockId) return "Choose the dish this receipe prepares.";
    if (!form.receipyById) return "Choose who prepared this receipe.";
    if (!form.ingredients.length) return "Add at least one ingredient.";
    if (form.ingredients.some((item) => !item.stockId || !item.conversionId || Number(item.quantity) <= 0))
      return "Every ingredient needs an item, unit, and quantity above zero.";
    if (new Set(form.ingredients.map((item) => item.stockId)).size !== form.ingredients.length)
      return "The same ingredient can only be added once.";
    if (form.quantityType === "Other" && Number(form.quantity) <= 0) return "Enter the custom serving quantity.";
    return "";
  };

  const save = async () => {
    const validation = validate();
    if (validation) { setNotice({ text: validation, error: true }); return; }
    setSaving(true);
    try {
      const currentIds = form.ingredients.map((item) => item.stockId);
      const payload = {
        apiToken: CART_INITIAL_STATE.apiToken, shopHash: shop.id, stockId: form.stockId,
        receipyById: form.receipyById, quantityType: form.quantityType,
        quantity: Number(form.quantity),
        listAddedItem: form.ingredients.map((item) => ({ ...item, quantity: Number(item.quantity), wastagePercent: Number(item.wastagePercent || 0) })),
        listDeletedStocks: form.originalIngredientIds.filter((id) => !currentIds.includes(id)),
        preparationSteps: form.preparationSteps.filter((step) => step.title || step.instruction).map((step, index) => ({
          ...step, stepNo: index + 1, durationMinutes: Number(step.durationMinutes || 0),
        })),
        preparationNotes: form.preparationNotes,
        preparationTimeMinutes: Number(form.preparationTimeMinutes || 0),
      };
      const response = form.id
        ? await snpAxios.put(`${api}receipe/${form.id}`, { jcJson: payload })
        : await snpAxios.post(`${api}receipe`, { jcJson: payload });
      if (response.data.status !== SUCCESS) throw new Error(response.data.errorMessage || "Receipe was not saved.");
      setOpen(false);
      setNotice({ text: form.id ? "Receipe updated." : "Receipe added.", error: false });
      await loadReceipes();
    } catch (error) {
      setNotice({ text: messageOf(error, "Receipe was not saved."), error: true });
    } finally { setSaving(false); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Remove the receipe for ${item.stockName}?`)) return;
    setSaving(true);
    try {
      const response = await snpAxios.delete(`${api}receipe/${item.id}`, { params });
      if (response.data.status !== SUCCESS) throw new Error("Receipe was not removed.");
      setNotice({ text: "Receipe removed.", error: false });
      await loadReceipes();
    } catch (error) { setNotice({ text: messageOf(error, "Receipe was not removed."), error: true }); }
    finally { setSaving(false); }
  };

  const filtered = receipes.filter((item) => `${item.stockName} ${item.receipeBy}`.toLowerCase().includes(search.toLowerCase()));

  return <div className="receipe-v2">
    {(loading || saving) && <Loader />}
    <header className="receipe-v2__hero">
      <div><span className="receipe-v2__kicker">Kitchen preparation</span><h1>Receipe Management</h1>
        <p>Keep every dish consistent with ingredients, portions, and clear cooking steps.</p></div>
      <button className="receipe-v2__primary" onClick={openNew}>+ Add receipe</button>
    </header>
    {notice.text && <div className={`receipe-v2__notice ${notice.error ? "is-error" : ""}`}>
      <span>{notice.text}</span><button onClick={() => setNotice({ text: "", error: false })}>×</button>
    </div>}
    <div className="receipe-v2__tools">
      <label><span>Find a dish</span><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by dish or cook" /></label>
      <div className="receipe-v2__count"><strong>{filtered.length}</strong><span>active receipes</span></div>
    </div>
    <main className="receipe-v2__grid">
      {filtered.map((item) => <article className="receipe-card" key={item.id}>
        <div className="receipe-card__top"><div className="receipe-card__dish">{item.stockName?.charAt(0) || "R"}</div>
          <div><span>Dish receipe</span><h2>{item.stockName || "Unnamed dish"}</h2></div></div>
        <div className="receipe-card__facts">
          <div><strong>{item.ingredients?.length || item.itemSize || 0}</strong><span>Ingredients</span></div>
          <div><strong>{item.preparationTimeMinutes || 0}</strong><span>Minutes</span></div>
          <div><strong>{item.quantityType || "Full"}</strong><span>Serving</span></div>
        </div>
        <p className="receipe-card__cook">Prepared by <strong>{item.receipeBy || "Kitchen team"}</strong></p>
        <div className="receipe-card__actions"><button onClick={() => openEdit(item.id)}>View & edit</button><button className="danger" onClick={() => remove(item)}>Remove</button></div>
      </article>)}
      {!loading && !filtered.length && <div className="receipe-v2__empty"><span>🍲</span><h2>No receipes found</h2><p>Add your first dish receipe or change the search.</p></div>}
    </main>

    {open && <div className="receipe-modal" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setOpen(false)}>
      <section className="receipe-modal__panel" role="dialog" aria-modal="true" aria-label="Receipe editor">
        <header><div><span>{form.id ? "Update kitchen guide" : "Create kitchen guide"}</span><h2>{form.id ? "Edit receipe" : "New receipe"}</h2></div><button onClick={() => setOpen(false)} aria-label="Close">×</button></header>
        <div className="receipe-modal__body">
          <section className="receipe-section"><div className="receipe-section__heading"><span>1</span><div><h3>Dish and serving</h3><p>Choose the menu dish and who owns the preparation.</p></div></div>
            <div className="receipe-fields">
              <label><span>Menu dish *</span><select value={form.stockId} disabled={Boolean(form.id)} onChange={(e) => setForm({ ...form, stockId: e.target.value })}><option value="">Choose a dish</option>{dishes.map((dish) => <option key={dish.rateCardId} value={dish.rateCardId}>{dish.tname}</option>)}</select></label>
              <label><span>Prepared by *</span><select value={form.receipyById} onChange={(e) => setForm({ ...form, receipyById: e.target.value })}><option value="">Choose a team member</option>{employees.map((employee) => <option key={employee.id} value={employee.id}>{employee.details}</option>)}</select></label>
              <label><span>Serving size</span><select value={form.quantityType} onChange={(e) => setForm({ ...form, quantityType: e.target.value })}>{Object.keys(foodQuantityTypes).map((type) => <option key={type}>{type}</option>)}</select></label>
              {form.quantityType === "Other" && <label><span>Custom serving quantity</span><input type="number" min="0.01" step="0.01" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></label>}
              <label><span>Total preparation time</span><div className="receipe-input-suffix"><input type="number" min="0" value={form.preparationTimeMinutes} onChange={(e) => setForm({ ...form, preparationTimeMinutes: e.target.value })} /><em>min</em></div></label>
            </div>
          </section>

          <section className="receipe-section"><div className="receipe-section__heading"><span>2</span><div><h3>Ingredients</h3><p>Enter quantities in the smallest stock unit used by your kitchen.</p></div></div>
            <div className="receipe-repeat">
              {form.ingredients.map((ingredient, index) => <div className="ingredient-row" key={`${index}-${ingredient.stockId}`}>
                <label className="wide"><span>Ingredient *</span><select value={ingredient.stockId} onChange={(e) => setIngredient(index, "stockId", e.target.value)}><option value="">Choose stock item</option>{stocks.map((stock) => <option key={stock.id} value={stock.id}>{stock.name}</option>)}</select></label>
                <label><span>Unit *</span><select value={ingredient.conversionId} onChange={(e) => setIngredient(index, "conversionId", e.target.value)}><option value="">Choose unit</option>{units.map((unit) => <option key={unit.id} value={unit.id}>{unitName(unit.id)}</option>)}</select></label>
                <label><span>Quantity *</span><input type="number" min="0.001" step="0.001" value={ingredient.quantity} onChange={(e) => setIngredient(index, "quantity", e.target.value)} /></label>
                <label><span>Wastage %</span><input type="number" min="0" max="100" value={ingredient.wastagePercent} onChange={(e) => setIngredient(index, "wastagePercent", e.target.value)} /></label>
                <label className="wide"><span>Kitchen note</span><input value={ingredient.notes} onChange={(e) => setIngredient(index, "notes", e.target.value)} placeholder="Washed, finely chopped…" /></label>
                <label className="receipe-check"><input type="checkbox" checked={ingredient.optional} onChange={(e) => setIngredient(index, "optional", e.target.checked)} /><span>Optional</span></label>
                <button className="receipe-icon-danger" disabled={form.ingredients.length === 1} onClick={() => removeIngredient(index)} aria-label="Remove ingredient">×</button>
              </div>)}
            </div>
            <button className="receipe-add-row" onClick={() => setForm((current) => ({ ...current, ingredients: [...current.ingredients, emptyIngredient()] }))}>+ Add another ingredient</button>
          </section>

          <section className="receipe-section"><div className="receipe-section__heading"><span>3</span><div><h3>Preparation method</h3><p>Write short steps that are easy to follow during service.</p></div></div>
            <div className="receipe-repeat">
              {form.preparationSteps.map((step, index) => <div className="step-row" key={index}><b>{index + 1}</b>
                <label><span>Step name</span><input value={step.title} onChange={(e) => setStep(index, "title", e.target.value)} placeholder="Prepare the base" /></label>
                <label className="wide"><span>Instruction</span><textarea rows="2" value={step.instruction} onChange={(e) => setStep(index, "instruction", e.target.value)} placeholder="Describe what the cook should do…" /></label>
                <label><span>Time</span><div className="receipe-input-suffix"><input type="number" min="0" value={step.durationMinutes} onChange={(e) => setStep(index, "durationMinutes", e.target.value)} /><em>min</em></div></label>
                <button className="receipe-icon-danger" onClick={() => removeStep(index)} aria-label="Remove step">×</button>
              </div>)}
            </div>
            <button className="receipe-add-row" onClick={() => setForm((current) => ({ ...current, preparationSteps: [...current.preparationSteps, emptyStep(current.preparationSteps.length + 1)] }))}>+ Add preparation step</button>
            <label className="receipe-notes"><span>Chef notes</span><textarea rows="3" value={form.preparationNotes} onChange={(e) => setForm({ ...form, preparationNotes: e.target.value })} placeholder="Plating, holding, allergy, or service notes…" /></label>
          </section>
        </div>
        <footer><button className="receipe-v2__secondary" onClick={() => setOpen(false)}>Cancel</button><button className="receipe-v2__primary" disabled={saving} onClick={save}>{saving ? "Saving…" : form.id ? "Save changes" : "Add receipe"}</button></footer>
      </section>
    </div>}
  </div>;
}
